import React, { MouseEventHandler, MutableRefObject, ReactNode } from 'react';
import styled, { css } from 'styled-components';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import { LoadingIconCoupon } from '../loading/LoadingIconCoupon';
import { typography } from '../../theme';
import { colors } from '../../theme/color';

export enum CheckboxSize {
  REGULAR,
  LARGE,
}

type Props = React.PropsWithChildren<{
  value: boolean;
  disabled?: boolean;
  loading?: boolean;
  onClick: MouseEventHandler<HTMLButtonElement>;
  checkBoxSize?: CheckboxSize;
  className?: string;
  inputRef?: MutableRefObject<HTMLButtonElement>;
  errorMessage?: string;
  testId?: string;
}>;

export const Checkbox: React.FC<Props> = ({
  onClick,
  children,
  value,
  checkBoxSize = CheckboxSize.REGULAR,
  className,
  inputRef,
  loading = false,
  disabled = false,
  errorMessage,
  testId,
}) => {
  const getCheckComponent = (): ReactNode => {
    if (loading) {
      return <LoadingIconCoupon />;
    } else if (value) {
      return <CheckBoxIcon />;
    } else {
      return <CheckBoxOutlineBlankIcon />;
    }
  };

  return (
    <Container>
      <RowContainer
        type="button"
        hasChildren={Boolean(children)}
        className={className}
        onClick={onClick}
        checkBoxSize={checkBoxSize}
        disabled={disabled || loading}
        ref={inputRef}
        role="checkbox"
        data-testid={testId}
        aria-checked={value ? 'true' : 'false'}
      >
        <CheckContainer error={Boolean(errorMessage)}>{getCheckComponent()}</CheckContainer>
        {children && <Label>{children}</Label>}
      </RowContainer>
      <ErrorMessage aria-live="polite" checkBoxSize={checkBoxSize}>
        {errorMessage}
      </ErrorMessage>
    </Container>
  );
};

const CheckContainer = styled.div<{ error?: boolean }>`
  color: ${({ error }) => (error ? colors.textError : colors.brandPrimary)};
  flex-shrink: 0;

  svg {
    width: 100%;
    height: 100%;
  }
`;

const Container = styled.div`
  margin: 8px 0;
`;

const RowContainer = styled.button<{ checkBoxSize: CheckboxSize; hasChildren: boolean }>`
  display: flex;
  flex-grow: 0;
  align-items: center;
  cursor: pointer;
  box-sizing: border-box;
  text-decoration: none;
  align-items: center;
  text-transform: initial;
  border: none;
  transition: color 0.1s ease-in-out;
  background-color: transparent;
  padding: 0;
  margin: 0;

  ${({ checkBoxSize }) => {
    switch (checkBoxSize) {
      case CheckboxSize.LARGE:
        return css`
          ${typography.textLargeStyle}
          ${CheckContainer} {
            width: 48px;
            height: 48px;
          }
        `;

      default:
        return css`
          ${typography.textStyle}
          ${CheckContainer} {
            width: 32px;
            height: 32px;
          }
        `;
    }
  }};

  &:focus-visible {
    ${CheckContainer} {
      outline: 2px solid ${colors.brandPrimary};
      border-radius: 2px;
    }
    outline: none;
  }

  color: ${colors.textDefault};
`;

const Label = styled.div`
  flex-grow: 1;
  margin-left: 8px;
  text-align: left;
`;

const ErrorMessage = styled.p<{ checkBoxSize: CheckboxSize }>`
  ${typography.textStyle}
  color: ${colors.textError};
  margin: ${({ checkBoxSize }) => {
    return checkBoxSize === CheckboxSize.REGULAR ? '-4px 0 0 40px' : '-8px 0 0 56px';
  }};
`;
